import { useEffect } from "react";
import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { addBreadcrumb } from "../../shared/utils/sentryBreadcrumbs";
import { trackVeriffDeviceContext, trackVeriffCanceled, flushSentry } from "../../shared/utils/sentryTracking";
import { isVeriffMobile } from "../../shared/utils/veriffEnvironment";
import { logger } from "../../shared/utils/logger";
import { trackViewed } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../shared/analytics/documentCaptureEvents";
import type { DocumentErrorPayload } from "../../shared/types";
import type { LegalIdVerificationState } from "./useLegalIdVerification";

interface UseVeriffEventsParams {
  veriffSessionUrl: string | null;
  state: LegalIdVerificationState;
  verificationId: string | null;
  sessionToken: string | null;
  lang?: string;
  setState: (state: LegalIdVerificationState) => void;
  onVerificationFinished: () => Promise<void>;
  onVerificationError: (errorCode: DocumentErrorPayload["errorCode"], message: string) => void;
  /** Ask the parent SDK to reload the host (top-level) page for camera-permission recovery. */
  requestHostReload: () => void;
}

/**
 * Custom hook to manage Veriff SDK event handling
 *
 * Analogous to useIProovEvents.ts for face verification.
 * Differences:
 * - Uses createVeriffFrame() callback instead of DOM event listeners
 * - Mounts only when state === "capturing" (after user clicks "Open camera")
 * - Uses embedded mode to render within the page on desktop; on mobile Veriff
 *   ignores embedded mode and renders its own modal, so we skip embedded there
 *   (and skip the #veriff-root container) — see isVeriffMobile().
 *
 * Events:
 * - MESSAGES.STARTED    → setState("capturing")
 * - MESSAGES.SUBMITTED  → setState("processing")
 * - MESSAGES.FINISHED   → setState("processing") + trigger poll
 * - MESSAGES.CANCELED   → handleVerificationError (no retry — mirrors mobile NavigationManager)
 * - MESSAGES.RELOAD_REQUEST → handled via onReload (host-page reload for
 *   camera-permission recovery, mainly Safari iOS); do NOT close the frame.
 */
export function useVeriffEvents({
  veriffSessionUrl,
  state,
  verificationId,
  sessionToken,
  lang,
  setState,
  onVerificationFinished,
  onVerificationError,
  requestHostReload,
}: UseVeriffEventsParams) {
  useEffect(() => {
    // Only mount Veriff when user has clicked "Open camera" (state === "capturing")
    if (!veriffSessionUrl || state !== "capturing") return;

    logger.log("[Veriff] Initializing Veriff SDK with session URL");

    addBreadcrumb("veriff-event", "Veriff SDK initializing", "info", {
      verificationId,
      hasUrl: !!veriffSessionUrl,
    });

    // Capture a device snapshot at capture start (camera count, permission,
    // viewport, etc.) so users who later abandon are still represented —
    // the result payload only exists for users who submit.
    trackVeriffDeviceContext(verificationId);

    // Veriff ignores embedded mode on mobile and renders its own modal; passing
    // embedded there would leave our #veriff-root container empty behind it.
    // Gate on Veriff's own mobile heuristic so both decisions stay consistent.
    const embedded = !isVeriffMobile();

    const veriffFrame = createVeriffFrame({
      url: veriffSessionUrl,
      ...(lang && { lang }),
      embedded,
      ...(embedded && { embeddedOptions: { rootElementID: "veriff-root" } }),
      // Documented camera-permission recovery (mainly Safari iOS): reload the
      // host page so the browser re-prompts for camera access on a fresh session.
      onReload: async () => {
        logger.log("[Veriff] Reload requested — asking host to reload for camera-permission recovery");
        addBreadcrumb("veriff-event", "Veriff reload request", "warning", { verificationId });
        await flushSentry();
        // Delegate to the parent: only a TOP-page reload re-prompts camera on iOS
        // Safari, and this nested iframe can't reload it cross-origin.
        requestHostReload();
      },
      onEvent: (msg) => {
        logger.log("[Veriff] Event received:", msg);

        addBreadcrumb("veriff-event", `Veriff ${msg}`, "info", { verificationId, event: msg });

        switch (msg) {
          case MESSAGES.STARTED:
            // Veriff's own app finished loading and is interactive — the vendor-ready
            // milestone, NOT a camera-permission signal (Veriff's own camera-permission
            // handling isn't observable from here — there is no separate signal for it).
            logger.log("[Veriff] Started");
            trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.DOCUMENT_CAPTURE_READY, "indicator", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });
            break;

          case MESSAGES.SUBMITTED:
            // User submitted documents to Veriff — keep state as "capturing" and wait for FINISHED
            // NOTE: do NOT setState("processing") here — that would change `state` in the useEffect
            // dependency array, triggering cleanup (veriffFrame.close()) before FINISHED fires,
            // which prevents onVerificationFinished() from ever being called.
            logger.log("[Veriff] Submitted");
            break;

          case MESSAGES.FINISHED:
            // Veriff flow complete — poll backend for decision
            // Mirrors mobile: NavigationManager transitions to .polling after Veriff completes
            logger.log("[Veriff] Finished — polling backend");
            trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.DOCUMENT_CAPTURE_COMPLETED, "indicator", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });
            setState("processing");
            onVerificationFinished();
            break;

          case MESSAGES.CANCELED:
            // User canceled — immediate error, no retry
            // Mirrors mobile: NavigationManager.swift:379-406 throws CancellationError
            logger.log("[Veriff] Canceled by user");
            addBreadcrumb("veriff-event", "Veriff canceled by user", "warning", { verificationId, sessionToken });
            trackVeriffCanceled(verificationId);
            onVerificationError("user_canceled", "Document verification was canceled");
            break;

          case MESSAGES.RELOAD_REQUEST:
            // Recovery is handled by onReload (host-page reload). Do NOT close
            // the frame here — closing without reloading is exactly what left
            // iOS Safari users stranded on an empty container.
            logger.log("[Veriff] Reload request received (handled by onReload)");
            break;
        }
      },
    });

    logger.log("[Veriff] SDK initialized successfully");

    // Cleanup mirrors iProov removeEventListener on unmount
    return () => {
      logger.log("[Veriff] Cleaning up Veriff SDK");
      veriffFrame.close();
    };
  }, [veriffSessionUrl, state, verificationId, sessionToken, lang, setState, onVerificationFinished, onVerificationError, requestHostReload]);
}
